import React, { useEffect, useState } from 'react';
import { Button, Descriptions, Drawer, message, Popconfirm, Space, Spin, Table, Tag } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import useAuthStore from '../store/authStore';
import { StockReceipt, StockReceiptItem as StockReceiptItemType, getReceipt, deleteReceipt, LEGAL_ENTITY_LABELS } from '../api/stock';

interface Props {
  open: boolean;
  onClose: () => void;
  receiptId: number | null;
  width?: number;
  refreshKey?: number;
  onDeleted?: () => void;
}

const StockReceiptInfoDrawer: React.FC<Props> = ({ open, onClose, receiptId, width = 540, refreshKey, onDeleted }) => {
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('orders:write');
  const [data, setData] = useState<StockReceipt | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !receiptId) return;
    setLoading(true);
    setData(null);
    getReceipt(receiptId).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [open, receiptId, refreshKey]);

  const handleDelete = async () => {
    if (!receiptId) return;
    try {
      await deleteReceipt(receiptId);
      message.success('Приход удалён, остатки скорректированы');
      onDeleted?.();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось удалить');
    }
  };

  const total = (data?.items || []).reduce(
    (s, it) => s + Number(it.quantity) * Number(it.price || 0),
    0,
  );

  return (
    <Drawer
      title={data ? `Приход #${data.id} от ${data.date}` : 'Приход'}
      open={open}
      onClose={onClose}
      width={width}
      extra={canWrite && data ? (
        <Popconfirm
          title="Удалить приход? Остатки будут уменьшены."
          onConfirm={handleDelete}
          okText="Да"
          cancelText="Нет"
        >
          <Button danger icon={<DeleteOutlined />}>Удалить</Button>
        </Popconfirm>
      ) : null}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : data && (
        <>
          <Space wrap style={{ marginBottom: 16 }}>
            <Tag color={data.legal_entity === 'autoregium' ? 'blue' : 'purple'}>
              {LEGAL_ENTITY_LABELS[data.legal_entity]}
            </Tag>
          </Space>

          <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Дата">{data.date}</Descriptions.Item>
            {data.supplier_name && (
              <Descriptions.Item label="Поставщик">{data.supplier_name}</Descriptions.Item>
            )}
            {data.invoice_number && (
              <Descriptions.Item label="Накладная №">{data.invoice_number}</Descriptions.Item>
            )}
            {data.notes && <Descriptions.Item label="Примечание">{data.notes}</Descriptions.Item>}
          </Descriptions>

          <Table
            size="small"
            rowKey="id"
            dataSource={data.items}
            pagination={false}
            columns={[
              { title: 'Товар', dataIndex: 'stock_item_name', key: 'name' },
              {
                title: 'Кол-во', dataIndex: 'quantity', key: 'quantity', width: 90, align: 'right' as const,
                render: (v: number) => Number(v).toLocaleString('ru-RU'),
              },
              {
                title: 'Цена', dataIndex: 'price', key: 'price', width: 110, align: 'right' as const,
                render: (v: number | null) => v != null ? `${Number(v).toLocaleString('ru-RU')} ₽` : '—',
              },
              {
                title: 'Сумма', key: 'sum', width: 110, align: 'right' as const,
                render: (_: unknown, r: StockReceiptItemType) => {
                  if (r.price == null) return '—';
                  return `${(Number(r.quantity) * Number(r.price)).toLocaleString('ru-RU')} ₽`;
                },
              },
            ]}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3} align="right"><b>Итого:</b></Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <b>{total.toLocaleString('ru-RU')} ₽</b>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </>
      )}
    </Drawer>
  );
};

export default StockReceiptInfoDrawer;
